import React from 'react';
import { Card, CardHeader, CardContent } from '@mui/material';
import Grid from '@mui/material/Grid';
import { pink, deepPurple, indigo, lightBlue, cyan, purple, green, orange } from '@mui/material/colors';
import { AutoStories, FormatListBulleted, QuestionAnswer, Quiz, Comment, CheckBox, Ballot, SkipNext, SportsScore } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import TypedValue from '../../TypedValue';
import IconStat from './IconStat';

const QuickStats = ({ pages, questions, answers, actions, comments, subheader = null }) => {
    const theme = useTheme();
    const textColour = theme.palette.common.white;

    const pageCount = pages?.length || 1;

    const averageNumberOfQuestionsPerPage = questions.length > 0 ? questions.length / pageCount : 0;

    const skippedQuestions = answers.reduce((sum, item) => {
        const nullCount = Object.values(item).filter(v => v === null).length;

        return sum + nullCount;
    }, 0);

    const averageQuestionsSkipped = answers.length > 0 ? skippedQuestions / answers.length : 0;

    const opens = actions.filter(e => e.actionType === 'Survey Open')?.length ?? 0;
    const completes = actions.filter(e => e.actionType === 'Survey Completed')?.length ?? 0;

    const completionRate = (opens === 0 || completes === 0) ? 0 : (completes / opens) * 100;

    return (
        <Card sx={{ height: '100%', pb: 1 }}>
            <CardHeader
                sx={{
                    m: 0,
                    py: 1,
                    '& .MuiCardHeader-action': {
                        alignSelf: 'center'
                    }
                }}
                slotProps={{
                    title: {
                        variant: 'h6',
                        fontWeight: 'normal'
                    }
                }}
                avatar={<FormatListBulleted />}
                title="Quick Takeaways"
                subheader={subheader ?? undefined}
            />
            <CardContent
                sx={{
                    m: 0,
                    px: 1,
                    py: 0,
                    '&:last-child': {
                        paddingBottom: 0
                    }
                }}
            >
                <Grid container spacing={1}>
                    <Grid size={{ xs: 4, md: 3 }}>
                        <IconStat
                            icon={(
                                <Quiz
                                    fontSize="large"
                                    sx={{ color: textColour }}
                                />
                            )}
                            figure={questions.length}
                            text={questions.length === 1 ? 'Question' : 'Questions'}
                            colour={indigo[500]}
                            textColour={textColour}
                        />
                    </Grid>
                    <Grid size={{ xs: 4, md: 3 }}>
                        <IconStat
                            icon={(
                                <AutoStories
                                    fontSize="large"
                                    sx={{ color: textColour }}
                                />
                            )}
                            figure={pageCount}
                            text={pageCount === 1 ? 'Page' : 'Pages'}
                            colour={purple[500]}
                            textColour={textColour}
                        />
                    </Grid>
                    <Grid size={{ xs: 4, md: 3 }}>
                        <IconStat
                            icon={(
                                <Ballot
                                    fontSize="large"
                                    sx={{ color: textColour }}
                                />
                            )}
                            figure={<TypedValue value={averageNumberOfQuestionsPerPage} type="number" accuracy={1} />}
                            text="Questions Per Page"
                            colour={pink[500]}
                            textColour={textColour}
                        />
                    </Grid>
                    <Grid size={{ xs: 4, md: 3 }}>
                        <IconStat
                            icon={(
                                <CheckBox
                                    fontSize="large"
                                    sx={{ color: textColour }}
                                />
                            )}
                            figure={questions.filter(e => e.isRequired).length}
                            text="Required Questions"
                            colour={cyan[600]}
                            textColour={textColour}
                        />
                    </Grid>
                    <Grid size={{ xs: 4, md: 3 }}>
                        <IconStat
                            icon={(
                                <QuestionAnswer
                                    fontSize="large"
                                    sx={{ color: textColour }}
                                />
                            )}
                            figure={<TypedValue value={answers.length} type="compactNumber" accuracy={0} />}
                            text={answers.length === 1 ? 'Answer' : 'Answers'}
                            colour={green[600]}
                            textColour={textColour}
                        />
                    </Grid>
                    <Grid size={{ xs: 4, md: 3 }}>
                        <IconStat
                            icon={(
                                <Comment
                                    fontSize="large"
                                    sx={{ color: textColour }}
                                />
                            )}
                            figure={comments.length}
                            text={comments.length === 1 ? 'Comment' : 'Comments'}
                            colour={lightBlue[500]}
                            textColour={textColour}
                        />
                    </Grid>
                    <Grid size={{ xs: 4, md: 3 }}>
                        <IconStat
                            icon={(
                                <SkipNext
                                    fontSize="large"
                                    sx={{ color: textColour }}
                                />
                            )}
                            figure={<TypedValue value={averageQuestionsSkipped} type="number" accuracy={1} />}
                            text="Average Skipped"
                            colour={orange[500]}
                            textColour={textColour}
                        />
                    </Grid>
                    <Grid size={{ xs: 4, md: 3 }}>
                        <IconStat
                            icon={(
                                <SportsScore
                                    fontSize="large"
                                    sx={{ color: textColour }}
                                />
                            )}
                            figure={<TypedValue value={completionRate} type="rate" accuracy={1} />}
                            text="Completion Rate"
                            colour={deepPurple[500]}
                            textColour={textColour}
                        />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

export default QuickStats;